import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search, Loader2 } from 'lucide-react';
import api from '../api/client';
import { resolveImage } from './admin/ImageUpload';

/**
 * Header search bar with debounced autocomplete.
 *
 * - 2+ characters triggers a `/products/suggest?q=` call (debounced 220ms).
 * - Dropdown renders up to 8 rows with thumbnails, name, brand, price.
 * - Enter (or clicking "View all results") submits the full search.
 * - Keyboard: ↓ / ↑ move highlight, Enter selects.
 */
const SearchBar = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const wrapRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const q = query.trim();
    if (q.length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    timerRef.current = setTimeout(async () => {
      try {
        const res = await api.get('/products/suggest', { params: { q, limit: 8 } });
        setResults(res.data || []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 220);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [query]);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const submit = (e) => {
    e?.preventDefault();
    if (highlight >= 0 && results[highlight]) {
      const r = results[highlight];
      navigate(`/products/${r.slug || r.id}`);
      setOpen(false);
      setQuery('');
      return;
    }
    if (query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
      setOpen(false);
    }
  };

  const onKey = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(results.length - 1, h + 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setHighlight(h => Math.max(-1, h - 1)); }
    if (e.key === 'Escape')    { setOpen(false); }
  };

  return (
    <form onSubmit={submit} ref={wrapRef} className="flex-1 max-w-3xl relative">
      <input
        type="text"
        value={query}
        onFocus={() => setOpen(true)}
        onChange={e => { setQuery(e.target.value); setOpen(true); setHighlight(-1); }}
        onKeyDown={onKey}
        placeholder="What are you looking for?"
        data-testid="header-search-input"
        aria-autocomplete="list"
        className="w-full border-2 border-[#12263A] rounded-sm py-2.5 pl-4 pr-12 text-sm focus:outline-none focus:border-[#F46A25]"
      />
      <button type="submit" data-testid="header-search-submit" className="absolute right-0 top-0 bottom-0 bg-[#12263A] text-white px-4 hover:bg-[#F46A25] transition-colors" aria-label="Search">
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
      </button>
      {open && query.trim().length >= 2 && (
        <div data-testid="search-suggestions" className="absolute left-0 right-0 top-full mt-1 bg-white border border-neutral-200 shadow-2xl rounded-sm max-h-[70vh] overflow-y-auto z-50">
          {results.length === 0 && !loading && (
            <div className="p-4 text-sm text-neutral-500 italic">No matches for "{query}". Try a different term.</div>
          )}
          {results.map((r, i) => (
            <Link
              key={r.id}
              to={`/products/${r.slug || r.id}`}
              onClick={() => { setOpen(false); setQuery(''); }}
              data-testid={`search-suggestion-${i}`}
              className={`flex items-center gap-3 px-3 py-2 hover:bg-orange-50 border-b border-neutral-100 last:border-b-0 ${i === highlight ? 'bg-orange-50' : ''}`}
            >
              <div className="w-10 h-10 bg-neutral-50 rounded-sm overflow-hidden flex-shrink-0">
                <img src={resolveImage(r.image)} alt="" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase text-neutral-500 font-bold leading-tight">{r.brand}</div>
                <div className="text-sm text-[#12263A] font-semibold truncate">{r.name}</div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-sm font-bold text-[#12263A]">£{Number(r.price || 0).toFixed(2)}</div>
                {r.wasPrice && r.wasPrice > r.price && (
                  <div className="text-[10px] text-neutral-500 line-through">£{Number(r.wasPrice).toFixed(2)}</div>
                )}
              </div>
            </Link>
          ))}
          {results.length > 0 && (
            <button
              type="submit"
              onClick={submit}
              data-testid="search-view-all"
              className="w-full text-left px-3 py-2 text-xs font-bold text-[#F46A25] hover:bg-orange-50 border-t border-neutral-100"
            >
              View all results for "{query}" →
            </button>
          )}
        </div>
      )}
    </form>
  );
};

export default SearchBar;
